import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { UserPlus, Search, Edit2, ToggleLeft, ToggleRight, X, Loader2, KeyRound, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { firstName: '', lastName: '', email: '', password: '', phone: '', department: '', position: '', role: 'employee', teamId: '', employeeId: '' };

export default function AdminEmployees() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetGenerated, setResetGenerated] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let p = '';
    const a = new Uint32Array(14);
    crypto.getRandomValues(a);
    for (let i = 0; i < 14; i++) p += chars[a[i] % chars.length];
    setResetPwd(p);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPwd.length < 8) { toast.error('Minimum 8 caractères'); return; }
    setResetLoading(true);
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { newPassword: resetPwd });
      setResetGenerated(resetPwd);
      toast.success('Mot de passe réinitialisé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally { setResetLoading(false); }
  };

  const closeResetModal = () => {
    setResetUser(null); setResetPwd(''); setResetGenerated(''); setResetCopied(false);
  };

  const copyResetPwd = async () => {
    try { await navigator.clipboard.writeText(resetGenerated); setResetCopied(true); setTimeout(() => setResetCopied(false), 2000); }
    catch { toast.error('Copie impossible'); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [usersRes, teamsRes] = await Promise.all([api.get('/users'), api.get('/teams')]);
    setUsers(usersRes.data);
    setTeams(teamsRes.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/users/${editing}`, form);
        toast.success('Employé mis à jour');
      } else {
        await api.post('/users', form);
        toast.success('Employé créé avec succès');
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Vous ne pouvez pas désactiver votre propre compte.');
      return;
    }
    if (user.isActive && !window.confirm(`Désactiver le compte de ${user.firstName} ${user.lastName} ?`)) return;
    try {
      await api.put(`/users/${user.id}`, { isActive: !user.isActive });
      fetchData();
      toast.success(user.isActive ? 'Compte désactivé' : 'Compte activé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const openEdit = (user) => {
    setForm({ ...user, password: '' });
    setEditing(user.id);
    setShowModal(true);
  };

  const filtered = users.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const set = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Employés</h1>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition">
          <UserPlus className="w-4 h-4" /> Ajouter un employé
        </button>
      </div>

      {/* Recherche */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="Rechercher un employé..." />
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {['Employé', 'Rôle', 'Équipe', 'Département', 'Statut', 'Actions'].map(h => (
                <th key={h} className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {u.avatar
                      ? <img src={u.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                          {u.firstName?.[0]}{u.lastName?.[0]}
                        </div>
                    }
                    <div>
                      <p className="font-medium text-gray-800">{u.firstName} {u.lastName}</p>
                      <p className="text-gray-400 text-xs">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    u.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{u.role}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{u.team?.name || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{u.department || '-'}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {u.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(u)} title="Modifier" className="text-blue-600 hover:text-blue-700 p-1.5 hover:bg-blue-50 rounded-lg">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setResetUser(u); setResetPwd(''); setResetGenerated(''); }} title="Réinitialiser le mot de passe"
                      className="text-amber-600 hover:text-amber-700 p-1.5 hover:bg-amber-50 rounded-lg">
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={u.id === currentUser?.id}
                      title={u.id === currentUser?.id ? 'Vous ne pouvez pas désactiver votre propre compte' : (u.isActive ? 'Désactiver' : 'Activer')}
                      className={`p-1.5 rounded-lg ${u.id === currentUser?.id ? 'opacity-30 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                    >
                      {u.isActive ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-800">{editing ? 'Modifier l\'employé' : 'Nouvel employé'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Prénom *</label>
                  <input value={form.firstName} onChange={set('firstName')} required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom *</label>
                  <input value={form.lastName} onChange={set('lastName')} required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={set('email')} required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              {!editing && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mot de passe</label>
                  <input type="password" value={form.password} onChange={set('password')} placeholder="ChangeMe123! par défaut" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                  <select value={form.role} onChange={set('role')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="employee">Employé</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Équipe</label>
                  <select value={form.teamId} onChange={set('teamId')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="">Aucune</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Département</label>
                  <input value={form.department} onChange={set('department')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Poste</label>
                  <input value={form.position} onChange={set('position')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">ID Employé</label>
                  <input value={form.employeeId} onChange={set('employeeId')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
                  <input type="tel" value={form.phone} onChange={set('phone')} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {editing ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Reset mot de passe */}
      {resetUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800">Réinitialiser le mot de passe</h2>
                  <p className="text-xs text-gray-500">{resetUser.firstName} {resetUser.lastName} · {resetUser.email}</p>
                </div>
              </div>
              <button onClick={closeResetModal} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {!resetGenerated ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-600">Nouveau mot de passe</label>
                    <button type="button" onClick={generatePassword} className="text-xs text-amber-600 hover:text-amber-700 font-medium">
                      Générer un mot de passe fort
                    </button>
                  </div>
                  <input type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} required minLength={8}
                    placeholder="Minimum 8 caractères" autoFocus
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-amber-500 outline-none" />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  ⚠️ L'utilisateur sera déconnecté de ses sessions actives à sa prochaine action. Communique-lui le nouveau mot de passe par un canal sécurisé.
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={closeResetModal}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Annuler</button>
                  <button type="submit" disabled={resetLoading}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                    {resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                    Réinitialiser
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  ✅ Mot de passe réinitialisé. <strong>Copie-le maintenant</strong>, il ne sera plus affiché.
                </div>
                <div className="relative">
                  <input readOnly value={resetGenerated} onClick={e => e.target.select()}
                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono" />
                  <button onClick={copyResetPwd} title="Copier"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
                    {resetCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <button onClick={closeResetModal}
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium">
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
